import { Weapon } from '../../Weapon.js';
import { FUSED_WEAPONS } from '../../../../../config/constants.js';

/**
 * Rubric (Harmonics fusion: Glyphpen + Nova Pulse) — the marks stop expiring
 * and start burning.
 *
 * The Glyphpen's problem as a base weapon is that a mark is a single hit on a
 * short fuse: write it, and whatever isn't standing there in the next 0.7s
 * never pays. Nova Pulse's contribution is the thing that fixes that — its
 * echoes are the game's other "damage that happens where you are, later" — so
 * the fused marks are lingering zones instead of one-shot discs: they re-tick
 * on a hit cooldown for as long as they burn, which is what makes the Rubric
 * worth several times what its `damage` field reads (see FUSED_WEAPONS' header
 * on re-ticking damage, and the bench).
 *
 * Structurally it keeps the Glyphpen's whole identity — positional, worthless
 * against something parked at range, excellent against anything that has to
 * come to you — and pays the fusion's value in dwell rather than in reach.
 */
export class Rubric extends Weapon {
  constructor() {
    super(FUSED_WEAPONS.rubric);
  }

  /**
   * Mark count is FLAT, unlike the Glyphpen's level-gated ladder. A burning
   * mark is worth many times a one-shot disc, so each extra one multiplies an
   * already-multiplied number: with marks on the Glyphpen's rungs this landed
   * at 8x its parents by max level. The ladder is spent on tick damage and on
   * how much floor a cast covers instead.
   */
  get markCount() {
    return this.config.marks;
  }

  /** The writing spreads: wider marks over more ground, at the same count. */
  get markRadius() {
    return this.config.projectileRadius + this.level * 0.8;
  }

  _markDamage() {
    return this.config.damage + this.level * this.config.damagePerLevel;
  }

  fire(origin, target, pool) {
    const angle = target
      ? Math.atan2(target.y - origin.y, target.x - origin.x)
      : Math.atan2(origin.facing?.y ?? -1, origin.facing?.x ?? 0);
    // Same fold-the-fire-rate-into-the-hit-cooldown trick the standing hazards
    // use (Halo, Orbit Ward): most of this weapon's damage comes out of a zone
    // re-ticking, so "+fire rate" has to speed the ticks up or it would only
    // buy slightly more overlapping marks.
    const fireRateMultiplier = (origin.surgeFireRateMultiplier ?? 1) * (origin.fireRateMultiplier ?? 1);
    for (let i = 0; i < this.markCount; i++) {
      const spread = ((i / Math.max(1, this.markCount - 1)) - 0.5) * 1.1;
      const dist = (this.config.range + this.level * 2) * (0.45 + 0.55 * ((i % 2) === 0 ? 1 : 0.6));
      pool.fire({
        x: origin.x + Math.cos(angle + spread) * dist,
        y: origin.y + Math.sin(angle + spread) * dist,
        vx: 0,
        vy: 0,
        radius: this.markRadius,
        damage: this._markDamage(),
        hits: 1e9,
        hitCooldownDuration: this.config.hitCooldownDuration / fireRateMultiplier,
        lifetime: this.config.lifetime,
        color: this.config.color,
      });
    }
    this.lastBurstRadius = this.config.range;
  }
}
